// Survey and report storage operations

import { 
    STORES, 
    openDatabase, 
    getAllFromStore, 
    getFromStore, 
    putToStore, 
    deleteFromStore, 
    clearStore,
    clearAllStores
} from './db-core';
import { syncHistoryStorage } from './sync-history-storage';

interface PendingSurvey {
    id: string;
    residentName: string;
    propertyAddress: string;
    workOrderId?: string;
    dueDate: string;
    uprn?: string;
    residentPhoneNumber?: string;
    residentEmailAddress?: string;
    notes?: string;
    createdAt: string;
}

interface LocalSurveyReport {
    surveyId: string;
    reportId: string;
    document: unknown;
    startedAt: string;
    lastModifiedAt: string;
    isSynced: boolean;
    syncedAt?: string;
    isComplete: boolean;
    userId: string;
    cachedSurveyDetails?: PendingSurvey;
    outboxStatus: number;
    outboxAddedAt?: string;
    syncAttemptCount: number;
    lastSyncError?: string;
}

export const indexedDbStorage = {
    async initialize(): Promise<boolean> {
        try {
            await openDatabase();
            return true;
        } catch (error) {
            console.error('[IndexedDB] Initialize failed:', error);
            return false;
        }
    },

    async getCachedSurveys(): Promise<string> {
        try {
            const surveys = await getAllFromStore<PendingSurvey>(STORES.CACHED_SURVEYS);
            return JSON.stringify(surveys);
        } catch (error) {
            console.error('[IndexedDB] getCachedSurveys failed:', error);
            return '[]';
        }
    },

    async getCachedSurvey(surveyId: string): Promise<string | null> {
        try {
            const survey = await getFromStore<PendingSurvey>(STORES.CACHED_SURVEYS, surveyId);
            return survey ? JSON.stringify(survey) : null;
        } catch (error) {
            console.error('[IndexedDB] getCachedSurvey failed:', error);
            return null;
        }
    },

    async cacheSurveys(surveysJson: string): Promise<boolean> {
        try {
            const surveys = JSON.parse(surveysJson) as PendingSurvey[];
            await clearStore(STORES.CACHED_SURVEYS);
            for (const survey of surveys) {
                await putToStore(STORES.CACHED_SURVEYS, survey);
            }
            return true;
        } catch (error) {
            console.error('[IndexedDB] cacheSurveys failed:', error);
            return false;
        }
    },

    async getLocalReports(): Promise<string> {
        try {
            const reports = await getAllFromStore<LocalSurveyReport>(STORES.LOCAL_REPORTS);
            return JSON.stringify(reports);
        } catch (error) {
            console.error('[IndexedDB] getLocalReports failed:', error);
            return '[]';
        }
    },

    async getLocalReport(surveyId: string): Promise<string | null> {
        try {
            const report = await getFromStore<LocalSurveyReport>(STORES.LOCAL_REPORTS, surveyId);
            return report ? JSON.stringify(report) : null;
        } catch (error) {
            console.error('[IndexedDB] getLocalReport failed:', error);
            return null;
        }
    },

    async saveLocalReport(reportJson: string): Promise<boolean> {
        try {
            const report = JSON.parse(reportJson) as LocalSurveyReport;
            await putToStore(STORES.LOCAL_REPORTS, report);
            return true;
        } catch (error) {
            console.error('[IndexedDB] saveLocalReport failed:', error);
            return false;
        }
    },

    async deleteLocalReport(surveyId: string): Promise<boolean> {
        try {
            await deleteFromStore(STORES.LOCAL_REPORTS, surveyId);
            return true;
        } catch (error) {
            console.error('[IndexedDB] deleteLocalReport failed:', error);
            return false;
        }
    },

    async getPendingSyncReports(): Promise<string> {
        try {
            const reports = await getAllFromStore<LocalSurveyReport>(STORES.LOCAL_REPORTS);
            const pending = reports.filter(r => r.isComplete && !r.isSynced);
            return JSON.stringify(pending);
        } catch (error) {
            console.error('[IndexedDB] getPendingSyncReports failed:', error);
            return '[]';
        }
    },

    async markReportAsSynced(surveyId: string): Promise<boolean> {
        try {
            const report = await getFromStore<LocalSurveyReport>(STORES.LOCAL_REPORTS, surveyId);
            if (report) {
                report.isSynced = true;
                report.syncedAt = new Date().toISOString();
                await putToStore(STORES.LOCAL_REPORTS, report);
            }
            return true;
        } catch (error) {
            console.error('[IndexedDB] markReportAsSynced failed:', error);
            return false;
        }
    },

    // Sync history operations (delegated to sync-history-storage)
    getSyncEvents: syncHistoryStorage.getSyncEvents.bind(syncHistoryStorage),
    getSyncEvent: syncHistoryStorage.getSyncEvent.bind(syncHistoryStorage),
    saveSyncEvent: syncHistoryStorage.saveSyncEvent.bind(syncHistoryStorage),
    addSyncLogEntry: syncHistoryStorage.addSyncLogEntry.bind(syncHistoryStorage),
    clearSyncHistory: syncHistoryStorage.clearSyncHistory.bind(syncHistoryStorage),

    // Clear all data
    async clearAllData(): Promise<boolean> {
        try {
            await clearAllStores();
            console.log('[IndexedDB] All data cleared');
            return true;
        } catch (error) {
            console.error('[IndexedDB] clearAllData failed:', error);
            return false;
        }
    }
};
